import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPricingRule,
  getBillingEvents,
  getBillingSummary,
  getTenantPricingRule,
  saveTenantPricingRule,
  deleteTenantPricingRule,
} from "@/api/endpoints/billing";
import { resizeVM } from "@/api/endpoints/vms";
import type { PricingRule, VMResizePayload } from "@/types/billing";
import { useActiveTenant } from "@/components/tenant/TenantSelector";

export function usePricingRule() {
  return useQuery({
    queryKey: ["pricing-rule"],
    queryFn: getPricingRule,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });
}

export function useBillingEvents(offset = 0, limit = 50) {
  const [tenantId] = useActiveTenant();

  return useQuery({
    queryKey: ["billing-events", tenantId, offset, limit],
    queryFn: () => getBillingEvents(offset, limit),
    enabled: !!tenantId,
  });
}

export function useBillingSummary(year: number, month: number) {
  const [tenantId] = useActiveTenant();

  return useQuery({
    queryKey: ["billing-summary", tenantId, year, month],
    queryFn: () => getBillingSummary(year, month),
    enabled: !!tenantId,
  });
}

// ── Tenant-specific Pricing ──────────────────────────────────────────────────

export function useTenantPricingRule(tenantId: number | null) {
  return useQuery({
    queryKey: ["tenant-pricing", tenantId],
    queryFn: () => getTenantPricingRule(tenantId!),
    enabled: tenantId != null,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveTenantPricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      payload,
    }: {
      tenantId: number;
      payload: Omit<PricingRule, "id" | "tenant_id" | "is_active" | "effective_from">;
    }) => saveTenantPricingRule(tenantId, payload),
    onSuccess: (_data, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ["tenant-pricing", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
}

export function useDeleteTenantPricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: number) => deleteTenantPricingRule(tenantId),
    onSuccess: (_data, tenantId) => {
      queryClient.invalidateQueries({ queryKey: ["tenant-pricing", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
}

export function useResizeVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vmId, payload }: { vmId: number; payload: VMResizePayload }) =>
      resizeVM(vmId, payload),
    onSuccess: (_data, { vmId }) => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["vm", vmId] });
      queryClient.invalidateQueries({ queryKey: ["billing-events"] });
      queryClient.invalidateQueries({ queryKey: ["billing-summary"] });
    },
  });
}
